import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import type { DateRange } from "react-day-picker";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../../ui/popover";
import { Calendar } from "../../../../../ui/calendar";
import { useCompanyDateFormat, useCompanyWeekStartsOn } from "../../../../../../utils/hooks/company";
import { cn } from "../../../../../../lib/utils";
import { firstToUpper } from "../../../../../../utils/helpers/string";

const modes = ["day", "week", "month", "custom"];

function DateFilters({ from, to, mode, setFilters }: any) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const weekStartsOn = useCompanyWeekStartsOn() as 0 | 1 | 2 | 3 | 4 | 5 | 6;
    const [open, setOpen] = useState(false);
    const [customDraft, setCustomDraft] = useState<DateRange | undefined>(undefined);

    const title: string = useMemo(() => {
        if (mode === "day") {
            return moment(from).format(dateFormat);
        }
        return `${moment(from).format(dateFormat)} - ${moment(to).format(dateFormat)}`;
    }, [mode, from, to, dateFormat]);

    const selected = useMemo(() => ({
        from: moment(from).toDate(),
        to: moment(to).toDate(),
    }), [from, to]);

    const { calendarMode, numberOfMonths }: any = useMemo(() => {
        const calendarMode = (["day", "week"].includes(mode) && modes.length === 1) ? "single" : "range";
        const numberOfMonths = (["day", "week"].includes(mode) && modes.length === 1) ? 1 : 2;
        return { calendarMode, numberOfMonths };
    }, [mode]);

    const prev = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if (mode === "custom") {
            const diff = moment(to).diff(moment(from), "days") + 1;
            const newFrom = moment(from).subtract(diff, "days").format("YYYY-MM-DD");
            const newTo = moment(to).subtract(diff, "days").format("YYYY-MM-DD");
            setFilters((prev: any) => ({ ...(prev || {}), from: newFrom, to: newTo }));
        } else {
            const newFrom = moment(from)
                .subtract(1, mode as moment.DurationInputArg2)
                .startOf(mode as moment.DurationInputArg2)
                .format("YYYY-MM-DD");
            const newTo = moment(from)
                .subtract(1, mode as moment.DurationInputArg2)
                .endOf(mode as moment.DurationInputArg2)
                .format("YYYY-MM-DD");
            setFilters((prev: any) => ({ ...(prev || {}), from: newFrom, to: newTo }));
        }
    }, [mode, from, to, setFilters]);

    const next = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if (mode === "custom") {
            const diff = moment(to).diff(moment(from), "days") + 1;
            const newFrom = moment(from).add(diff, "days").format("YYYY-MM-DD");
            const newTo = moment(to).add(diff, "days").format("YYYY-MM-DD");
            setFilters((prev: any) => ({ ...(prev || {}), from: newFrom, to: newTo }));
        } else {
            const newFrom = moment(from)
                .add(1, mode as moment.DurationInputArg2)
                .startOf(mode as moment.DurationInputArg2)
                .format("YYYY-MM-DD");
            const newTo = moment(from)
                .add(1, mode as moment.DurationInputArg2)
                .endOf(mode as moment.DurationInputArg2)
                .format("YYYY-MM-DD");
            setFilters((prev: any) => ({ ...(prev || {}), from: newFrom, to: newTo }));
        }
    }, [mode, from, to, setFilters]);

    const onSelect = useCallback((range: DateRange, selectedDay?: Date) => {
        if (mode === "day") {
            const formattedDate = moment(selectedDay).format("YYYY-MM-DD");
            setFilters((prev: any) => ({ ...(prev || {}), from: formattedDate, to: formattedDate }));
        } else if (mode === "custom") {
            setCustomDraft(range);
            if (range?.from && range?.to) {
                const from = moment(range.from).format("YYYY-MM-DD");
                const to = moment(range.to).format("YYYY-MM-DD");
                setFilters((prev: any) => ({ ...(prev || {}), from, to }));
            }
        } else {
            const from = moment(selectedDay)
                .startOf(mode as moment.unitOfTime.StartOf)
                .format("YYYY-MM-DD");
            const to = moment(selectedDay)
                .endOf(mode as moment.unitOfTime.StartOf)
                .format("YYYY-MM-DD");
            setFilters((prev: any) => ({ ...(prev || {}), from, to }));
        }
    }, [mode, setFilters]);

    const onModeChange = useCallback((value: any) => {
        setCustomDraft(undefined);
        let newFrom, newTo;
        switch (value) {
            case "day":
                newFrom = moment().format("YYYY-MM-DD");
                newTo = moment().format("YYYY-MM-DD");
                break;
            case "month":
            case "custom":
                newFrom = moment().startOf("month").format("YYYY-MM-DD");
                newTo = moment().endOf("month").format("YYYY-MM-DD");
                break;
            default:
            case "week":
                newFrom = moment().startOf("week").format("YYYY-MM-DD");
                newTo = moment().endOf("week").format("YYYY-MM-DD");
                break;
        }
        setFilters((prev: any) => ({
            ...(prev || {}),
            mode: value,
            from: newFrom,
            to: newTo
        }));
    }, [setFilters]);

    return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-flex-wrap">
            <div className="tw-flex tw-border tw-border-input tw-rounded-lg tw-overflow-hidden tw-text-sm">
                {modes.map((option, i) => (
                    <button
                        key={option}
                        type="button"
                        className={cn(
                            "tw-px-3 tw-py-1.5 tw-cursor-pointer tw-transition-colors",
                            i > 0 && "tw-border-l tw-border-input",
                            mode === option
                                ? "tw-bg-primary/10 tw-text-primary tw-font-medium"
                                : "tw-text-muted-foreground hover:tw-text-foreground hover:tw-bg-muted/50"
                        )}
                        onClick={() => onModeChange(option)}
                    >
                        {firstToUpper(t(option))}
                    </button>
                ))}
            </div>

            <Popover
                open={open}
                onOpenChange={(next) => {
                    setOpen(next);
                    if (next && mode === "custom") {
                        setCustomDraft(undefined);
                    }
                }}
            >
                <PopoverTrigger asChild>
                    <div className="tw-flex tw-items-center tw-border tw-border-input tw-rounded-lg tw-overflow-hidden tw-text-sm tw-cursor-pointer tw-select-none">
                        <div
                            className="tw-px-2 tw-py-1.5 hover:tw-bg-muted/50 tw-transition-colors tw-border-r tw-border-input"
                            onClick={prev}
                        >
                            <ChevronLeft className="tw-size-4" />
                        </div>
                        <span className="tw-px-3 tw-py-1.5 tw-font-medium hover:tw-bg-muted/50 tw-transition-colors tw-whitespace-nowrap">
                            {title}
                        </span>
                        <div
                            className="tw-px-2 tw-py-1.5 hover:tw-bg-muted/50 tw-transition-colors tw-border-l tw-border-input"
                            onClick={next}
                        >
                            <ChevronRight className="tw-size-4" />
                        </div>
                    </div>
                </PopoverTrigger>
                <PopoverContent className="tw-w-full tw-p-5" align="start">
                    <div className="tw-flex tw-items-start tw-gap-3">
                        <Calendar
                            mode={calendarMode}
                            selected={mode === "custom" ? customDraft : selected}
                            onSelect={onSelect}
                            numberOfMonths={numberOfMonths}
                            weekStartsOn={weekStartsOn}
                            disabled={() => false}
                            required={true}
                            defaultMonth={moment(from).toDate()}
                        />
                        {modes.length > 1 && (
                            <div className="tw-flex tw-flex-col tw-py-1">
                                {modes.map((item) => (
                                    <span
                                        key={item}
                                        className={cn(
                                            "tw-px-3 tw-rounded-lg tw-py-1.5 tw-mb-0.5 tw-text-sm tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-transition-colors",
                                            mode === item
                                                ? "tw-bg-primary tw-text-white"
                                                : "tw-text-muted-foreground hover:tw-text-foreground hover:tw-bg-muted/50",
                                            item === "pay-group" && "tw-hidden",
                                        )}
                                        onClick={() => onModeChange(item)}
                                    >
                                        {item === mode ? (
                                            <Check size={14} />
                                        ) : (
                                            <div className="tw-w-3.5" />
                                        )}
                                        {t(item)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}

export default DateFilters;
